import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Columns, Check, ArrowUp, ArrowDown, ArrowUpDown, ArrowDownUp, X } from 'lucide-react';
import type { ValidationQueueEntry, TaxonomyEntry } from '../../types';
import { SortDialog, type SortableColumn } from './SortDialog';
import { parseSort, formatSort, toggleLevel, MAX_SORT_LEVELS } from '../../utils/sortLevels';
import { ValidationRow } from './ValidationRow';

interface ValidationGridProps {
  entries: ValidationQueueEntry[];
  taxonomy: TaxonomyEntry[];
  /** Map from part_id → pending selected_category_id (undefined = untouched) */
  editMap: Map<string, string | null>;
  onDecisionChange: (partId: string, selectedCategoryId: string | null) => void;
  pageOffset: number;
  /** Current sort param, e.g. 'part_name_asc'. Optional — omit for non-sortable usages. */
  sort?: string;
  /** Called when the user clicks a column header to change sort order. */
  onSortChange?: (sort: string) => void;
}

interface ColumnDef {
  key: string;
  label: string;
  width: string;
  hideable: boolean;
  /** Backend field name used for sort params (e.g. 'part_name'). Omit for non-sortable columns. */
  sortField?: string;
}

const COLUMNS: ColumnDef[] = [
  { key: 'index',            label: '#',                width: 'w-10', hideable: false },
  // Status sorts by urgency, not alphabetically — PDR-054. Placed first so a
  // reviewer scanning the page sees what still needs them.
  { key: 'status',           label: 'Status',           width: 'w-28', hideable: true,  sortField: 'review_status'          },
  { key: 'part_number',      label: 'Part Number',      width: 'w-36', hideable: true,  sortField: 'part_number'            },
  { key: 'part_name',        label: 'Part Name',        width: 'w-48', hideable: true,  sortField: 'part_name'              },
  { key: 'description',      label: 'Description',      width: 'w-72', hideable: true,  sortField: 'short_description'      },
  { key: 'current_category', label: 'Current Category', width: 'w-44', hideable: true,  sortField: 'current_category_name'  },
  { key: 'ai_category_1',    label: 'AI Category 1',    width: 'w-48', hideable: true,  sortField: 'ai_category_1_name'     },
  { key: 'ai_category_2',    label: 'AI Category 2',    width: 'w-48', hideable: true,  sortField: 'ai_category_2_name'     },
  { key: 'ai_specific',      label: 'AI Specific',      width: 'w-48', hideable: true,  sortField: 'ai_category_3'          },
  { key: 'final_category',   label: 'Final Category',   width: 'w-60', hideable: false, sortField: 'selected_category_name' },
];

/** The sortable columns, in header order — the dialog offers these. */
const SORTABLE_COLUMNS: SortableColumn[] = COLUMNS
  .filter((c) => c.sortField)
  .map((c) => ({ field: c.sortField as string, label: c.label }));
const SORTABLE_FIELDS: string[] = SORTABLE_COLUMNS.map((c) => c.field);

/** Column label for a backend field — the UI never shows a field name (CLAUDE.md §10). */
const sortLabel = (field: string): string =>
  SORTABLE_COLUMNS.find((c) => c.field === field)?.label ?? field;

const LS_KEY = 'validation-grid-hidden-cols';

export const ValidationGrid: React.FC<ValidationGridProps> = ({
  entries,
  taxonomy,
  editMap,
  onDecisionChange,
  pageOffset,
  sort = '',
  onSortChange = () => {},
}) => {
  // Initialise from localStorage so column visibility persists across navigation
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(LS_KEY);
      return saved ? new Set(JSON.parse(saved) as string[]) : new Set();
    } catch {
      return new Set();
    }
  });
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [showSortDialog, setShowSortDialog] = useState(false);

  const pickerRef    = useRef<HTMLDivElement>(null);
  const sortRef      = useRef<HTMLDivElement>(null);
  const topScrollRef = useRef<HTMLDivElement>(null);
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const phantomRef   = useRef<HTMLDivElement>(null);

  // Persist hidden columns to localStorage whenever the selection changes
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify([...hiddenColumns]));
    } catch { /* ignore quota errors */ }
  }, [hiddenColumns]);

  // Close the column picker when clicking outside
  useEffect(() => {
    if (!showColumnPicker) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowColumnPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showColumnPicker]);

  // Close the sort dialog when clicking outside (same pattern as the picker)
  useEffect(() => {
    if (!showSortDialog) return;
    const handler = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setShowSortDialog(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSortDialog]);

  // Keep the phantom div width in sync so the top scrollbar mirrors the table
  useEffect(() => {
    const update = () => {
      if (tableWrapRef.current && phantomRef.current) {
        phantomRef.current.style.width = `${tableWrapRef.current.scrollWidth}px`;
      }
    };
    update();
    const t = setTimeout(update, 120);
    return () => clearTimeout(t);
  }, [entries, hiddenColumns]);

  const onTopScroll = useCallback(() => {
    if (tableWrapRef.current && topScrollRef.current) {
      tableWrapRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  }, []);

  const onTableScroll = useCallback(() => {
    if (topScrollRef.current && tableWrapRef.current) {
      topScrollRef.current.scrollLeft = tableWrapRef.current.scrollLeft;
    }
  }, []);

  const toggleColumn = (key: string) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const visibleColumns = COLUMNS.filter((c) => !hiddenColumns.has(c.key));

  // The whole sort lives in one param; these helpers are the only readers.
  const levels = useMemo(() => parseSort(sort, SORTABLE_FIELDS), [sort]);
  const levelFor = (field: string) => levels.find((l) => l.field === field);
  const levelIndex = (field: string) => levels.findIndex((l) => l.field === field);

  // Shift-click adds a level; a plain click means "just sort by this" and
  // replaces the lot (PDR-055).
  const handleColumnSort = (sortField: string, additive: boolean) => {
    onSortChange(formatSort(toggleLevel(levels, sortField, additive)));
  };

  const SortIcon = ({ sortField }: { sortField: string }) => {
    const level = levelFor(sortField);
    if (level && !level.desc) return <ArrowUp   className="w-3 h-3 shrink-0" />;
    if (level && level.desc)  return <ArrowDown  className="w-3 h-3 shrink-0" />;
    // 60%, not 30%: at 30% the affordance read as decoration and reviewers
    // did not discover that headers sort at all (PDR-054).
    return <ArrowUpDown className="w-3 h-3 shrink-0 opacity-60 group-hover:opacity-100" />;
  };

  return (
    <div>
      {/* Sort + column toolbar */}
      <div className="flex items-center justify-between gap-2 mb-2">
        {/* Sort summary doubles as the dialog trigger, so the active sort is
            always visible without opening anything — reviewers were not
            discovering the header controls at all (PDR-054/PDR-055). */}
        <div className="relative" ref={sortRef}>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setShowSortDialog((v) => !v)}
              data-testid="sort-open-dialog"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-border text-text-secondary hover:bg-bg-subtle transition-colors"
            >
              <ArrowDownUp className="w-4 h-4" />
              {levels.length === 0 ? (
                'Sort'
              ) : (
                <span data-testid="sort-summary">
                  {'Sorted by: '}
                  {levels
                    .map((l) => `${sortLabel(l.field)} ${l.desc ? '↓' : '↑'}`)
                    .join(', then ')}
                </span>
              )}
            </button>

            {levels.length > 0 && (
              <button
                type="button"
                onClick={() => onSortChange('')}
                data-testid="sort-clear"
                aria-label="Clear sorting"
                title="Clear sorting"
                className="p-1.5 rounded-lg text-text-tertiary hover:text-qc-error hover:bg-bg-subtle transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {showSortDialog && (
            <SortDialog
              levels={levels}
              columns={SORTABLE_COLUMNS}
              onChange={(next) => onSortChange(formatSort(next))}
            />
          )}
        </div>

        <div className="relative" ref={pickerRef}>
          <button
            type="button"
            onClick={() => setShowColumnPicker((v) => !v)}
            data-testid="column-toggle-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-border text-text-secondary hover:bg-bg-subtle transition-colors"
          >
            <Columns className="w-4 h-4" />
            Columns
            {hiddenColumns.size > 0 && (
              <span className="ml-0.5 px-1.5 py-0.5 text-xs bg-interactive-primary/10 text-interactive-primary rounded-full">
                {hiddenColumns.size} hidden
              </span>
            )}
          </button>

          {showColumnPicker && (
            <div
              className="absolute right-0 top-full mt-1 z-30 w-52 bg-bg-surface rounded-lg border border-border shadow-lg py-1"
              role="dialog"
              aria-label="Toggle column visibility"
            >
              <p className="px-3 pt-1.5 pb-2 text-xs font-semibold text-text-tertiary uppercase tracking-wide border-b border-border">
                Show / Hide Columns
              </p>
              {COLUMNS.filter((c) => c.hideable).map((col) => {
                const visible = !hiddenColumns.has(col.key);
                return (
                  <button
                    key={col.key}
                    type="button"
                    onClick={() => toggleColumn(col.key)}
                    data-testid={`column-toggle-${col.key}`}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-subtle transition-colors"
                  >
                    <span
                      className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                        visible
                          ? 'bg-interactive-primary border-interactive-primary text-text-inverse'
                          : 'border-border'
                      }`}
                    >
                      {visible && <Check className="w-3 h-3" />}
                    </span>
                    {col.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Mirror scrollbar — appears above the table for easy horizontal scrolling */}
      <div
        ref={topScrollRef}
        onScroll={onTopScroll}
        className="overflow-x-scroll overflow-y-hidden rounded-t border border-b-0 border-border"
        style={{ height: 12 }}
        aria-hidden="true"
      >
        <div ref={phantomRef} style={{ height: 1 }} />
      </div>

      {/* Table */}
      <div
        ref={tableWrapRef}
        onScroll={onTableScroll}
        className="overflow-x-auto rounded-b border border-border"
      >
        <table
          className="w-full min-w-max text-sm border-collapse"
          data-testid="validation-grid"
        >
          <thead className="bg-bg-subtle sticky top-0 z-10">
            <tr>
              {visibleColumns.map((col) =>
                col.sortField ? (
                  <th
                    key={col.key}
                    // aria-sort is how a screen reader announces the active
                    // column; without it the sort is invisible to one.
                    aria-sort={
                      levelFor(col.sortField)
                        ? levelFor(col.sortField)!.desc
                          ? 'descending'
                          : 'ascending'
                        : 'none'
                    }
                    className={`${col.width} px-2 py-2 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide border-b border-border`}
                  >
                    <button
                      type="button"
                      onClick={(e) => handleColumnSort(col.sortField!, e.shiftKey)}
                      data-testid={`sort-col-${col.key}`}
                      // Reviewers were not finding these controls: the header
                      // looked like plain text and the arrow sat at 30%
                      // opacity. The title says what a click does, and the
                      // arrow is now legible before hover.
                      title={`Sort by ${col.label} (shift-click to add a level)`}
                      className="group inline-flex items-center gap-1 cursor-pointer hover:text-text-primary transition-colors"
                    >
                      {col.label}
                      <SortIcon sortField={col.sortField} />
                      {/* Priority is only meaningful once more than one column sorts. */}
                      {levels.length > 1 && levelIndex(col.sortField) >= 0 && (
                        <span
                          data-testid={`sort-priority-${col.key}`}
                          className="ml-0.5 px-1 min-w-[1rem] text-[10px] leading-4 font-semibold rounded-full bg-interactive-primary/10 text-interactive-primary"
                        >
                          {levelIndex(col.sortField) + 1}
                        </span>
                      )}
                    </button>
                  </th>
                ) : (
                  <th
                    key={col.key}
                    className={`${col.width} px-2 py-2 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide border-b border-border`}
                  >
                    {col.label}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr>
                <td
                  colSpan={visibleColumns.length}
                  className="px-4 py-8 text-center text-sm text-text-tertiary italic"
                >
                  No entries match the current filter.
                </td>
              </tr>
            )}
            {entries.map((entry, idx) => (
              <ValidationRow
                key={entry.part_id}
                entry={entry}
                rowIndex={pageOffset + idx}
                pendingDecision={editMap.get(entry.part_id)}
                taxonomy={taxonomy}
                onDecisionChange={onDecisionChange}
                hiddenColumns={hiddenColumns}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
